import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const formatAmount = (n, currency = 'XOF') => {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(n);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PARTY_CONFIG = {
  buyer: {
    label: 'Acheteur',
    icon: 'User',
    color: 'blue',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    badgeColor: 'bg-blue-100 text-blue-700 border-blue-200',
    signedBg: 'bg-blue-50 border-blue-300',
  },
  seller: {
    label: 'Vendeur',
    icon: 'Home',
    color: 'emerald',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    badgeColor: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    signedBg: 'bg-emerald-50 border-emerald-300',
  },
  notary: {
    label: 'Notaire',
    icon: 'Scale',
    color: 'purple',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    badgeColor: 'bg-purple-100 text-purple-700 border-purple-200',
    signedBg: 'bg-purple-50 border-purple-300',
  },
};

// ─── Signature Card ───────────────────────────────────────────────────────────
const SignatureCard = ({ party, signature, onSign, onRevoke, isAdmin, loading }) => {
  const cfg = PARTY_CONFIG?.[party];
  const isSigned = !!signature?.signed_at;

  return (
    <div className={`rounded-xl border-2 p-5 transition-all ${isSigned ? cfg?.signedBg : 'bg-white border-gray-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg?.iconBg}`}>
            <Icon name={cfg?.icon} size={20} className={cfg?.iconColor} />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">{cfg?.label}</p>
            {signature?.signer_name && (
              <p className="text-xs text-gray-500 mt-0.5">{signature?.signer_name}</p>
            )}
            {signature?.signer_email && (
              <p className="text-xs text-gray-400 font-mono">{signature?.signer_email}</p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          {isSigned ? (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.badgeColor}`}>
              <Icon name="CheckCircle" size={11} />
              Signé
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-gray-100 text-gray-500 border-gray-200">
              <Icon name="Clock" size={11} />
              En attente
            </span>
          )}
        </div>
      </div>

      {isSigned && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Icon name="Calendar" size={12} />
            <span>Signé le {formatDate(signature?.signed_at)}</span>
          </div>
          {signature?.ip_address && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Icon name="Globe" size={12} />
              <span className="font-mono">{signature?.ip_address}</span>
            </div>
          )}
          {signature?.signature_hash && (
            <div className="flex items-start gap-2 text-xs text-gray-400">
              <Icon name="Hash" size={12} className="mt-0.5 flex-shrink-0" />
              <span className="font-mono break-all">{signature?.signature_hash}</span>
            </div>
          )}
          {signature?.note && (
            <div className="mt-2 p-2 bg-white rounded-lg border border-gray-200">
              <p className="text-xs text-gray-600 italic">"{signature?.note}"</p>
            </div>
          )}
          {isAdmin && (
            <button
              onClick={() => onRevoke(party)}
              disabled={loading}
              className="mt-2 flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
            >
              <Icon name="RotateCcw" size={11} />
              Révoquer la signature
            </button>
          )}
        </div>
      )}

      {!isSigned && (
        <div className="mt-4">
          <button
            onClick={() => onSign(party)}
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed
              ${party === 'buyer' ? 'bg-blue-600 hover:bg-blue-700' : party === 'seller' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'}`}
          >
            {loading ? (
              <Icon name="Loader2" size={15} className="animate-spin" />
            ) : (
              <Icon name="PenLine" size={15} />
            )}
            Signer en tant que {cfg?.label}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Sign Confirmation Modal ──────────────────────────────────────────────────
const SignConfirmModal = ({ party, transaction, onConfirm, onCancel, loading }) => {
  const [note, setNote] = useState('');
  const [agreed, setAgreed] = useState(false);
  const cfg = PARTY_CONFIG?.[party];

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${cfg?.iconBg} flex items-center justify-center`}>
              <Icon name={cfg?.icon} size={20} className={cfg?.iconColor} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Signature Numérique — {cfg?.label}</h3>
              <p className="text-sm text-gray-500">Réf: {transaction?.reference || transaction?.id?.slice(0, 8)}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Transaction summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Montant séquestre</span>
              <span className="font-bold text-gray-900">{formatAmount(transaction?.amount_total || transaction?.total_amount, transaction?.currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Bénéficiaire</span>
              <span className="font-medium text-gray-700">Agent / Notaire</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Propriété</span>
              <span className="font-medium text-gray-700 truncate max-w-[180px]">{transaction?.title || '—'}</span>
            </div>
          </div>

          {/* Legal notice */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800 leading-relaxed">
              <strong>Avertissement légal :</strong> En signant ce document, vous autorisez irrévocablement le transfert des fonds séquestre vers le compte bancaire de l'agent/notaire désigné. Cette action est définitive et conforme aux termes du contrat de vente.
            </p>
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Note (optionnel)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e?.target?.value)}
              rows={2}
              placeholder="Commentaire ou réserve éventuelle..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Agreement checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e?.target?.checked)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Je confirme avoir lu et accepté les termes du transfert. Ma signature numérique a valeur légale conformément à la réglementation en vigueur.
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => onConfirm({ note })}
              disabled={loading || !agreed}
              className={`flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                ${party === 'buyer' ? 'bg-blue-600 hover:bg-blue-700' : party === 'seller' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'}`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="Loader2" size={14} className="animate-spin" />
                  Signature...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="PenLine" size={14} />
                  Confirmer la signature
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const EscrowDigitalSignatureModal = ({ transaction, onClose, onTransferApproved, isAdmin = true }) => {
  const [signatures, setSignatures] = useState({ buyer: null, seller: null, notary: null });
  const [loading, setLoading] = useState(false);
  const [loadingParty, setLoadingParty] = useState(null);
  const [signingParty, setSigningParty] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferDone, setTransferDone] = useState(false);
  const [error, setError] = useState(null);

  const allSigned = signatures?.buyer?.signed_at && signatures?.seller?.signed_at && signatures?.notary?.signed_at;

  const loadSignatures = useCallback(async () => {
    if (!transaction?.id) return;
    setLoading(true);
    try {
      const { data } = await supabase
        ?.from('escrow_digital_signatures')
        ?.select('*')
        ?.eq('transaction_id', transaction?.id);

      const sigMap = { buyer: null, seller: null, notary: null };
      data?.forEach(sig => { sigMap[sig.party_role] = sig; });
      setSignatures(sigMap);
    } catch (err) {
      console.error('Error loading signatures:', err);
    } finally {
      setLoading(false);
    }
  }, [transaction?.id]);

  useEffect(() => { loadSignatures(); }, [loadSignatures]);

  // ─── Send signature confirmation emails to all 3 parties ─────────────────
  const sendSignatureEmails = useCallback(async (signedParty, signatureData, updatedSignatures) => {
    try {
      const ESCROW_EMAIL_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-escrow-emails`;
      const { data: sessionData } = await supabase?.auth?.getSession();
      const accessToken = sessionData?.session?.access_token;
      const headers = { 'Content-Type': 'application/json' };
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

      const totalSigned = ['buyer', 'seller', 'notary']?.filter(p => updatedSignatures?.[p]?.signed_at)?.length;
      const allNowSigned = totalSigned === 3;

      // Fetch party contact info from transaction or escrow_digital_signatures
      const partyContacts = {
        buyer: {
          email: transaction?.buyer_email || transaction?.client_email || null,
          name: updatedSignatures?.buyer?.signer_name || 'Acheteur',
        },
        seller: {
          email: transaction?.seller_email || null,
          name: updatedSignatures?.seller?.signer_name || 'Vendeur',
        },
        notary: {
          email: transaction?.notary_email || null,
          name: updatedSignatures?.notary?.signer_name || 'Notaire',
        },
      };

      const emailPromises = ['buyer', 'seller', 'notary']?.map(async (role) => {
        const contact = partyContacts?.[role];
        if (!contact?.email) return;
        return fetch(ESCROW_EMAIL_URL, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            type: 'digital_signature_confirmation',
            data: {
              recipient_email: contact?.email,
              recipient_name: contact?.name,
              recipient_role: role,
              transaction_ref: transaction?.reference || transaction?.id?.slice(0, 8)?.toUpperCase(),
              property_title: transaction?.title || transaction?.item_title,
              amount: transaction?.amount_total || transaction?.total_amount,
              currency: transaction?.currency || 'XOF',
              signed_by_role: signedParty,
              signed_by_name: signatureData?.signer_name || PARTY_CONFIG?.[signedParty]?.label,
              signature_timestamp: signatureData?.signed_at,
              signature_hash: signatureData?.signature_hash,
              total_signatures: totalSigned,
              required_signatures: 3,
              all_signed: allNowSigned,
              transaction_id: transaction?.id,
            },
          }),
        });
      });

      await Promise.allSettled(emailPromises);
    } catch (err) {
      console.warn('[EscrowSignature] Email notification failed (non-blocking):', err?.message);
    }
  }, [transaction]);

  const handleSign = async (party, { note }) => {
    setLoadingParty(party);
    setError(null);
    try {
      // Generate a simple hash for the signature
      const hashInput = `${transaction?.id}-${party}-${Date.now()}`;
      const signatureHash = btoa(hashInput)?.slice(0, 32);

      const { error: upsertError } = await supabase
        ?.from('escrow_digital_signatures')
        ?.upsert({
          transaction_id: transaction?.id,
          party_role: party,
          signed_at: new Date()?.toISOString(),
          signature_hash: signatureHash,
          note: note || null,
          signer_name: PARTY_CONFIG?.[party]?.label,
        }, { onConflict: 'transaction_id,party_role' });

      if (upsertError) throw upsertError;

      // Log the action
      await supabase?.from('escrow_action_log')?.insert({
        transaction_id: transaction?.id,
        action_type: 'digital_signature',
        performed_by: party,
        notes: `Signature numérique apposée par ${PARTY_CONFIG?.[party]?.label}${note ? ` — Note: ${note}` : ''}`,
      });

      await loadSignatures();

      // Build updated signatures map for email context
      const updatedSigs = {
        ...signatures,
        [party]: { signed_at: new Date()?.toISOString(), signature_hash: signatureHash, signer_name: PARTY_CONFIG?.[party]?.label },
      };

      // Send confirmation emails to all parties (non-blocking)
      sendSignatureEmails(party, { signed_at: new Date()?.toISOString(), signature_hash: signatureHash, signer_name: PARTY_CONFIG?.[party]?.label }, updatedSigs);

      setSigningParty(null);
    } catch (err) {
      setError(`Erreur lors de la signature : ${err?.message}`);
    } finally {
      setLoadingParty(null);
    }
  };

  const handleRevoke = async (party) => {
    if (!window.confirm(`Révoquer la signature de ${PARTY_CONFIG?.[party]?.label} ?`)) return;
    setLoadingParty(party);
    try {
      await supabase
        ?.from('escrow_digital_signatures')
        ?.delete()
        ?.eq('transaction_id', transaction?.id)
        ?.eq('party_role', party);
      await loadSignatures();
    } catch (err) {
      setError(`Erreur lors de la révocation : ${err?.message}`);
    } finally {
      setLoadingParty(null);
    }
  };

  const handleTransfer = async () => {
    if (!allSigned) return;
    setTransferLoading(true);
    setError(null);
    try {
      // Update transaction status to RELEASED
      const { error: updateError } = await supabase
        ?.from('escrow_transactions')
        ?.update({
          status: 'RELEASED',
          escrow_release_date: new Date()?.toISOString(),
          admin_notes: 'Libération approuvée — 3 signatures numériques obtenues (acheteur, vendeur, notaire)',
        })
        ?.eq('id', transaction?.id);

      if (updateError) throw updateError;

      // Log the transfer
      await supabase?.from('escrow_action_log')?.insert({
        transaction_id: transaction?.id,
        action_type: 'funds_transfer_approved',
        performed_by: 'system',
        notes: 'Transfert des fonds séquestre approuvé — toutes les signatures numériques obtenues',
      });

      setTransferDone(true);
      onTransferApproved?.();
    } catch (err) {
      setError(`Erreur lors du transfert : ${err?.message}`);
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
                <Icon name="PenLine" size={22} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Signatures Numériques Requises</h2>
                <p className="text-sm text-gray-500">Réf: {transaction?.reference || transaction?.id?.slice(0, 8)}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
              <Icon name="X" size={22} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Transaction summary banner */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-amber-900">{transaction?.title || 'Transaction Séquestre'}</p>
                <p className="text-2xl font-bold text-amber-800 mt-1">
                  {formatAmount(transaction?.amount_total || transaction?.total_amount, transaction?.currency)}
                </p>
                <p className="text-xs text-amber-600 mt-1">Fonds en attente de transfert vers compte agent/notaire</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-amber-600">Signatures obtenues</p>
                <p className="text-3xl font-bold text-amber-800">
                  {[signatures?.buyer, signatures?.seller, signatures?.notary]?.filter(s => s?.signed_at)?.length}
                  <span className="text-lg font-normal text-amber-600">/3</span>
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-amber-600 mb-1">
                <span>Progression des signatures</span>
                <span>{Math.round(([signatures?.buyer, signatures?.seller, signatures?.notary]?.filter(s => s?.signed_at)?.length / 3) * 100)}%</span>
              </div>
              <div className="h-2 bg-amber-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all duration-500"
                  style={{ width: `${([signatures?.buyer, signatures?.seller, signatures?.notary]?.filter(s => s?.signed_at)?.length / 3) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Transfer done */}
          {transferDone ? (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <Icon name="CheckCircle" size={36} className="text-green-500" />
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-green-700">Transfert Approuvé !</p>
                <p className="text-sm text-gray-500 mt-1">Les fonds séquestre ont été libérés vers le compte agent/notaire.</p>
              </div>
              <button onClick={onClose} className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-colors">
                Fermer
              </button>
            </div>
          ) : (
            <>
              {/* Signature cards */}
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {['buyer', 'seller', 'notary']?.map(party => (
                    <SignatureCard
                      key={party}
                      party={party}
                      signature={signatures?.[party]}
                      onSign={(p) => setSigningParty(p)}
                      onRevoke={handleRevoke}
                      isAdmin={isAdmin}
                      loading={loadingParty === party}
                    />
                  ))}
                </div>
              )}

              {/* Transfer button */}
              <div className={`rounded-xl border-2 p-5 transition-all ${allSigned ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className={`text-sm font-bold ${allSigned ? 'text-green-800' : 'text-gray-500'}`}>
                      {allSigned ? '✅ Toutes les signatures obtenues' : '⏳ En attente des signatures'}
                    </p>
                    <p className={`text-xs mt-0.5 ${allSigned ? 'text-green-600' : 'text-gray-400'}`}>
                      {allSigned
                        ? 'Le transfert des fonds peut être initié.'
                        : `${3 - [signatures?.buyer, signatures?.seller, signatures?.notary]?.filter(s => s?.signed_at)?.length} signature(s) manquante(s)`}
                    </p>
                  </div>
                  <button
                    onClick={handleTransfer}
                    disabled={!allSigned || transferLoading}
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                  >
                    {transferLoading ? (
                      <Icon name="Loader2" size={16} className="animate-spin" />
                    ) : (
                      <Icon name="ArrowRightCircle" size={16} />
                    )}
                    Initier le Transfert
                  </button>
                </div>
              </div>

              {/* Info note */}
              <p className="text-xs text-gray-400 text-center">
                Les trois parties (acheteur, vendeur, notaire) doivent apposer leur signature numérique avant tout transfert de fonds séquestre.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Sign confirmation sub-modal */}
      {signingParty && (
        <SignConfirmModal
          party={signingParty}
          transaction={transaction}
          onConfirm={(data) => handleSign(signingParty, data)}
          onCancel={() => setSigningParty(null)}
          loading={loadingParty === signingParty}
        />
      )}
    </div>
  );
};

export default EscrowDigitalSignatureModal;
